import type { V4HSiteId } from "@/data/common/ids";

import { computed } from "vue";

import { useDashboardDateRange } from "@/composables/dashboard/use-dashboard-date-range";
import { useGetMultisiteConsumptionQuery } from "@/composables/kili/consumption/queries/use-get-multisite-consumption-query";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { EDataUnit } from "@/constants/data-units.type";
import {
  TMultisiteConsumptionRequest,
  TSiteConsumption,
} from "@/data/api/kili/consumption/types/multisite-consumption.type";
import { EPrefixSymbol, getMaybeValuePrefixSymbol, getValueByPrefix } from "@/utils/translation-utils";

export interface IConsumptionOverviewSiteRow {
  id: V4HSiteId;
  name: string;
  sectionCount: number | null;
  applianceCount: number | null;
  consumptionValue: number | null;
  consumptionLabel: string;
}

export function useConsumptionOverviewSites() {
  const { dateRange } = useDashboardDateRange();
  const request = computed<TMultisiteConsumptionRequest>(() => ({
    dateRange: {
      ...dateRange.value,
    },
    siteIds: undefined,
  }));

  const { data: consumption, isLoading: isConsumptionLoading } = useGetMultisiteConsumptionQuery(request);
  const { data: sites, isLoading: isSitesLoading } = useGetSitesQuery();

  const rows = computed<IConsumptionOverviewSiteRow[]>(() => {
    const consumptionBySite = consumption.value?.bySite ?? [];
    const consumptionValues = consumptionBySite
      .map(({ consumption }) => consumption.value)
      .filter((value): value is number => typeof value === "number");
    const highestConsumption = consumptionValues.length > 0 ? Math.max(...consumptionValues) : undefined;
    const prefixSymbol = getMaybeValuePrefixSymbol(highestConsumption);
    const metricsBySiteId = new Map(consumptionBySite.map((site) => [site.siteId, site]));

    return (sites.value ?? []).map((site) => {
      const metrics = metricsBySiteId.get(site.siteId);

      return {
        id: site.siteId,
        name: site.name,
        sectionCount: metrics ? (metrics.sectionCount ?? 0) : null,
        applianceCount: metrics ? (metrics.applianceCount ?? 0) : null,
        consumptionValue: metrics?.consumption.value ?? null,
        consumptionLabel: formatConsumption(metrics?.consumption, prefixSymbol),
      };
    });
  });

  const isLoading = computed(() => isSitesLoading.value || isConsumptionLoading.value);
  const mainConsumptionSource = computed(() => consumption.value?.mainConsumptionSource ?? "fallback");

  return { isLoading, mainConsumptionSource, rows, sites };
}

function formatConsumption(consumption?: TSiteConsumption["consumption"], prefixSymbol?: EPrefixSymbol) {
  const value = typeof consumption?.value === "number" ? getValueByPrefix(consumption.value, prefixSymbol) : "-";

  return `${value} ${prefixSymbol ?? ""}${EDataUnit.WATT_HOUR}`;
}
